import { Component, ElementRef, OnDestroy, OnInit, SecurityContext, ViewChild } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { MVListingConfigs, MVOrdering, MVPagination, SortDirection } from 'is-lib';
import { finalize, forkJoin, Subject, takeUntil } from 'rxjs';
import { supportedFileUploadTypes } from 'src/app/constants';
import { CustomValidators } from 'src/app/functions/form-validators';
import { SnackBarType } from 'src/app/models/Snackbar';
import { CreateTicketMessage, ITicketMessage, Ticket, TicketFile, TicketMessage } from 'src/app/models/Ticket';
import { AlertsService } from 'src/app/services/alerts.service';
import { LoaderConfig } from 'src/app/services/generic.service';
import { SnackBarService } from 'src/app/services/snackbar.service';
import { TicketsService } from 'src/app/services/tickets.service';
import { UsersService } from 'src/app/services/users.service';
import { FileViewerComponent } from '../form/ticket-form.component';

export enum MessageStatus {
    Pending,
    Sent,
    Read,
    Error
}

const MAX_FILE_SIZE_BYTES = 2 * 1024 * 1024;
const ATTACHMENT_FILE_TYPE_ID = '1';
const TICKET_PAGE_SIZE = 10;
const MESSAGE_LOADER_ID = 'messageLoader';
const TICKET_LIST_LOADER_ID = 'ticketListLoader';
const DEFAULT_AVATAR = 'assets/media/avatars/blank.png';
const MESSAGE_BOX_MAX_HEIGHT_PX = 120;

/** Ticket status (lowercased) → theme colour used by the status badge */
const STATUS_COLORS: { [status: string]: string } = {
    pending: 'warning',
    inprogress: 'info',
    concluded: 'success',
    canceled: 'danger',
    cancelled: 'danger'
};

/** Ticket statuses (lowercased) listed under the finished group. Every other status (pending, inprogress) is in progress */
const FINISHED_STATUSES = ['concluded', 'canceled', 'cancelled'];

@Component({
    selector: 'app-ticket-messager',
    templateUrl: './ticket-messager.component.html',
    styleUrls: ['./ticket-messager.component.scss']
})
export class TicketMessagerComponent implements OnInit, OnDestroy {
    @ViewChild('messagesScroller') messagesScroller?: ElementRef<HTMLDivElement>;
    @ViewChild('messageBox') messageBox?: ElementRef<HTMLTextAreaElement>;

    readonly MessageStatus = MessageStatus;
    readonly supportedFileTypes = supportedFileUploadTypes;
    readonly defaultAvatar = DEFAULT_AVATAR;
    readonly messageLoaderId = MESSAGE_LOADER_ID;
    readonly ticketListLoaderId = TICKET_LIST_LOADER_ID;

    readonly userAvatar?: SafeUrl;

    ticketList: Ticket[] = [];
    ticketsLoading = false;
    ticketMessages: { [ticketId: number]: TicketMessage[] } = {};
    selectedTicketId?: number;

    messageControl = new FormControl<string>('', { nonNullable: true, validators: [Validators.required, CustomValidators.stringIsNotEmpty()] });
    messageFilesControl = new FormControl<File[]>([], { nonNullable: true });

    dragOver = false;
    filesBeingHovered = 0;

    private readonly _loadingTicketIds = new Set<number>();
    private readonly _destroy$ = new Subject<void>();

    constructor(
        private _ticketsService: TicketsService,
        private _userService: UsersService,
        private _sanitizer: DomSanitizer,
        private _alertsService: AlertsService,
        private _snackbarService: SnackBarService,
        private _route: ActivatedRoute,
        private _router: Router,
        private _dialog: MatDialog
    ) {
        const avatar = this._userService.userData?.avatar;
        if (avatar) {
            this.userAvatar = this._sanitizer.bypassSecurityTrustUrl('data:image/jpeg;base64,' + avatar);
        }
    }

    ngOnInit(): void {
        this._route.queryParams
            .pipe(takeUntil(this._destroy$))
            .subscribe(params => {
                const ticketId: string = params['ticketId'];
                if (!String.isNullOrUndefinedOrWhitespace(ticketId)) {
                    this.selectTicket(+ticketId);
                }
            });

        this._alertsService.messageSubject$
            .pipe(takeUntil(this._destroy$))
            .subscribe(message => this._onIncomingMessage(message));

        this.loadTickets();
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    get selectedTicket(): Ticket | undefined {
        return this.ticketList.find(ticket => ticket.id === this.selectedTicketId);
    }

    get selectedMessages(): TicketMessage[] {
        return this.selectedTicketId != null ? this.ticketMessages[this.selectedTicketId] ?? [] : [];
    }

    get files(): File[] {
        return this.messageFilesControl.value;
    }

    get canSend(): boolean {
        return this.selectedTicketId != null && this.messageControl.valid;
    }

    //#region APIRequests
    loadTickets(): void {
        const listingConfigs = new MVListingConfigs([], new MVOrdering('Number', SortDirection.desc), new MVPagination(0, TICKET_PAGE_SIZE));

        this.ticketsLoading = true;
        this._ticketsService.getTickets(listingConfigs, new LoaderConfig(true, false, TICKET_LIST_LOADER_ID))
            .pipe(
                takeUntil(this._destroy$),
                finalize(() => this.ticketsLoading = false)
            )
            .subscribe(sr => {
                this.ticketList = sr.data ?? [];

                if (this.selectedTicketId == null && this.ticketList.length > 0) {
                    this.selectTicket(this.ticketList[0].id);
                }
            });
    }

    loadTicketMessages(ticketId: number): void {
        if (this.ticketMessages[ticketId] != null || this._loadingTicketIds.has(ticketId)) {
            return;
        }

        this._loadingTicketIds.add(ticketId);
        this._ticketsService.getTicketMessages(ticketId, new LoaderConfig(true, false, MESSAGE_LOADER_ID))
            .pipe(
                takeUntil(this._destroy$),
                finalize(() => this._loadingTicketIds.delete(ticketId))
            )
            .subscribe(sr => {
                this.ticketMessages[ticketId] = (sr.data ?? []).map(message => this._mapServerMessage(message, ticketId));
            });
    }

    sendMessage(): void {
        const ticketId = this.selectedTicketId;
        if (ticketId == null || !this.canSend) {
            return;
        }

        const user = this._userService.userData!;
        const files = [...this.files];
        const message = new TicketMessage(this.messageControl.value, user.email, `${user.firstName} ${user.lastName}`, new Date(), [], false, undefined, ticketId);

        this.ticketMessages[ticketId] = [...this.ticketMessages[ticketId] ?? [], message];
        this.messageControl.reset();
        this.messageFilesControl.reset();
        this._resizeMessageBox();
        this._scrollToBottom();

        this._ticketsService.postTicketMessage(ticketId, new CreateTicketMessage(message.body)).subscribe({
            next: sr => {
                message.id = sr.data;

                if (files.length === 0) {
                    message.sentDate = new Date();
                    return;
                }

                this._uploadMessageFiles(ticketId, message, files);
            },
            error: () => message.errorOnSend = true
        });
    }

    private _uploadMessageFiles(ticketId: number, message: TicketMessage, files: File[]): void {
        const fileRequests = files.map(file => {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('typeId', ATTACHMENT_FILE_TYPE_ID);

            return this._ticketsService.postTicketMessageFile(ticketId, message.id!, formData);
        });

        forkJoin(fileRequests).subscribe({
            next: responses => {
                responses.forEach((sr, i) => {
                    if (sr.data) {
                        const file = files[i];
                        message.files.push(new TicketFile('', this._getExtension(file.name) ?? '', this._getBaseName(file.name), sr.data.path, new Date()));
                    }
                });
                message.sentDate = new Date();
            },
            error: () => message.errorOnSend = true
        });
    }
    //#endregion

    //#region Tickets
    selectTicket(ticketId: number): void {
        this.selectedTicketId = ticketId;
        this.loadTicketMessages(ticketId);
        this._scrollToBottom();
    }

    createNewTicket(): void {
        this._router.navigate(['tickets', 'new']);
    }

    get ticketGroups(): { label: string, tickets: Ticket[] }[] {
        const isFinished = (ticket: Ticket) => FINISHED_STATUSES.includes(ticket.status?.toLowerCase());

        return [
            { label: 'IN_PROGRESS', tickets: this.ticketList.filter(ticket => !isFinished(ticket)) },
            { label: 'FINISHED', tickets: this.ticketList.filter(ticket => isFinished(ticket)) }
        ].filter(group => group.tickets.length > 0);
    }

    getStatusClass(status: string): string {
        return `status-${STATUS_COLORS[status?.toLowerCase()] ?? 'default'}`;
    }

    trackByTicket(_: number, ticket: Ticket): number {
        return ticket.id;
    }
    //#endregion

    //#region Messages
    isOwnMessage(message: TicketMessage): boolean {
        return this._userService.userData?.email === message.createdBy;
    }

    /** True when the message is the first of a run of consecutive messages from the same sender on the same day */
    isGroupStart(messages: TicketMessage[], index: number): boolean {
        const previous = messages[index - 1];
        const current = messages[index];

        return previous == null
            || previous.isSystemMessage
            || current.isSystemMessage
            || previous.createdBy !== current.createdBy
            || this.isNewDay(messages, index);
    }

    isNewDay(messages: TicketMessage[], index: number): boolean {
        const previous = messages[index - 1];
        return previous == null || !this._isSameDay(new Date(previous.creationDate), new Date(messages[index].creationDate));
    }

    /** Returns an i18n key for today/yesterday, or null when the date should be printed instead */
    getDayLabelKey(date: Date): string | null {
        const today = new Date();
        const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
        const messageDate = new Date(date);

        if (this._isSameDay(messageDate, today)) {
            return 'TODAY';
        }
        if (this._isSameDay(messageDate, yesterday)) {
            return 'YESTERDAY';
        }
        return null;
    }

    getMessageStatus(message: TicketMessage): MessageStatus {
        if (message.errorOnSend) {
            return MessageStatus.Error;
        }
        if (message.readDate != null) {
            return MessageStatus.Read;
        }
        return message.sentDate != null ? MessageStatus.Sent : MessageStatus.Pending;
    }

    viewFile(file: TicketFile): void {
        this._dialog.open(FileViewerComponent, {
            data: {
                dataType: file.extension,
                fileUrl: file.path
            }
        });
    }

    trackByMessage(index: number, message: TicketMessage): number | string {
        return message.id ?? `pending-${index}`;
    }

    handleKeydown(event: KeyboardEvent): void {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            this.sendMessage();
        }
    }

    handleMessageInput(): void {
        this._resizeMessageBox();
    }
    //#endregion

    //#region Files
    handleFileInput(fileInput: HTMLInputElement): void {
        this._addFiles(fileInput.files);
        fileInput.value = '';
    }

    handleDragEnter(event: DragEvent): void {
        if (!this._isFileDrag(event)) {
            return;
        }

        event.preventDefault();
        this.filesBeingHovered = event.dataTransfer?.items.length ?? 0;
        this.dragOver = true;
    }

    /** Required on every dragover for the browser to allow the drop */
    handleDragOver(event: DragEvent): void {
        if (this._isFileDrag(event)) {
            event.preventDefault();
        }
    }

    handleDragLeave(): void {
        this.dragOver = false;
    }

    handleFileDrop(event: DragEvent): void {
        event.preventDefault();
        this.dragOver = false;
        this._addFiles(event.dataTransfer?.files ?? null);
    }

    removeFile(file: File): void {
        this.messageFilesControl.setValue(this.files.filter(f => f !== file));
    }

    /** Accepts either a bare extension or a full file name */
    getFileIcon(fileNameOrExtension: string): string {
        switch (fileNameOrExtension.split('.').pop()?.toLowerCase()) {
            case 'pdf': return 'fa-file-pdf';
            case 'doc': return 'fa-file-word';
            case 'xls': return 'fa-file-excel';
            case 'zip':
            case 'rar': return 'fa-file-zipper';
            case 'png':
            case 'jpg':
            case 'jpeg':
            case 'gif': return 'fa-file-image';
            default: return 'fa-file';
        }
    }

    formatFileSize(bytes: number): string {
        if (bytes >= 1024 * 1024) {
            return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
        }
        if (bytes >= 1024) {
            return `${(bytes / 1024).toFixed(1)} KB`;
        }
        return `${bytes} B`;
    }

    private _addFiles(fileList: FileList | null): void {
        if (!fileList || fileList.length === 0) {
            return;
        }

        const currentFiles = this.files;
        const newFiles = Array.from(fileList).filter(file => !currentFiles.some(existing => existing.name === file.name));
        const validFiles = newFiles.filter(file => this._isValidFile(file));
        const invalidFileCount = newFiles.length - validFiles.length;

        if (invalidFileCount > 0) {
            this._snackbarService.callSnackBar('INVALID_FILES_NOT_SAVED', SnackBarType.WARNING, undefined, undefined, undefined, { fileCount: invalidFileCount });
        }

        this.messageFilesControl.setValue([...currentFiles, ...validFiles]);
    }

    private _isFileDrag(event: DragEvent): boolean {
        return event.dataTransfer?.types.includes('Files') ?? false;
    }

    private _isValidFile(file: File): boolean {
        const extension = this._getExtension(file.name);
        return extension != null && this.supportedFileTypes.includes(`.${extension}`) && file.size <= MAX_FILE_SIZE_BYTES;
    }

    /** Returns the lowercase extension without the dot */
    private _getExtension(fileName: string): string | null {
        const dotIndex = fileName.lastIndexOf('.');
        return dotIndex > 0 ? fileName.substring(dotIndex + 1).toLowerCase() : null;
    }

    private _getBaseName(fileName: string): string {
        const dotIndex = fileName.lastIndexOf('.');
        return dotIndex > 0 ? fileName.substring(0, dotIndex) : fileName;
    }
    //#endregion

    //#region Helpers
    private _mapServerMessage(message: ITicketMessage, ticketId: number): TicketMessage {
        const picture = message.picture
            ? this._sanitizer.sanitize(SecurityContext.URL, 'data:image;base64,' + message.picture) ?? undefined
            : undefined;

        return new TicketMessage(
            message.body,
            message.creation.username,
            message.creation.userDisplayName,
            message.creation.date,
            message.files ?? [],
            message.isSystemMessage,
            message.id,
            ticketId,
            picture,
            message.readDate,
            message.creation.date
        );
    }

    private _onIncomingMessage(message: TicketMessage): void {
        const ticketId = message.ticketId;
        const messages = ticketId != null ? this.ticketMessages[ticketId] : undefined;

        // Messages for tickets that were never opened are fetched in full when the ticket is selected
        if (ticketId == null || messages == null) {
            return;
        }
        if (message.id != null && messages.some(m => m.id === message.id)) {
            return;
        }

        // SignalR delivers a plain object, so rebuild it to get the class behaviour
        messages.push(new TicketMessage(
            message.body,
            message.createdBy,
            message.createdByName,
            message.creationDate,
            message.files ?? [],
            message.isSystemMessage,
            message.id,
            ticketId,
            message.picture,
            message.readDate,
            message.creationDate
        ));

        if (ticketId === this.selectedTicketId) {
            this._scrollToBottom();
        }
    }

    private _isSameDay(a: Date, b: Date): boolean {
        return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
    }

    /** The message list uses column-reverse, so scrollTop 0 is the bottom */
    private _scrollToBottom(): void {
        setTimeout(() => this.messagesScroller?.nativeElement.scrollTo({ top: 0, behavior: 'smooth' }));
    }

    private _resizeMessageBox(): void {
        const textarea = this.messageBox?.nativeElement;
        if (!textarea) {
            return;
        }

        textarea.style.height = 'auto';
        textarea.style.height = `${Math.min(textarea.scrollHeight, MESSAGE_BOX_MAX_HEIGHT_PX)}px`;
    }
    //#endregion
}
